// src/prototype/enterprise/reports/reportsModel.ts
// ============================================================
// ENTERPRISE · REPORTS — a LIBRARY of governance-ready outputs that accumulates over time.
//
// listReports() enumerates the library (drawn from getScenarios()/getRuns() so titles/dates
// match the other surfaces); getReport(id) builds the rendered detail (one-pager + board deck).
//
//  · Q2 "Capital Reprioritization" — the REAL report: the saved Q2 scenario applied through
//    scenarioModel (cap $50M → $40M = −20%/−$10M; 40 → 33 funded; 7 crossed). Default selected.
//  · Q1 "Baseline Planning Summary" and "+6-Month Actuals Review" — SEEDED run reports built
//    from that run's portfolio + decisions (getPortfolioAsOf / getDecisionLog / getChangeDelta).
//    Honest-note: these are lighter, run-sourced summaries — real adapter numbers, no live lever
//    state. All read-only.
// ============================================================

import { getScenarios, getRuns, getRunCap, getPortfolioAsOf, getChangeDelta, getCapitalAsks } from '../../data/adapters/enterpriseSpine';
import { loadScenarioBase, computeScenario, Q2_WEIGHTS } from '../scenarios/scenarioModel';
import { buildDecisionTimeline, DECISION_BADGE, DECISION_DOT, type DecisionKind } from '../decisions/decisionsModel';

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthYearLong = (iso: string) => `${MONTHS_LONG[+iso.slice(5, 7) - 1]} ${iso.slice(0, 4)}`;
const monthYearShort = (iso: string) => `${MONTHS_SHORT[+iso.slice(5, 7) - 1]} ${iso.slice(0, 4)}`;

export interface ReportEntry {
  id: string;
  title: string;
  date: string; // "Apr 2026"
  source: string; // "Saved scenario · Q2 Budget Reduction" / "Run · Q1 2026 Baseline Planning"
  kind: 'scenario' | 'run';
  types: string[]; // ['Exec One-Pager · PDF', 'Board Rationale · PPTX']
  scenarioId?: string;
  runId?: string;
}

export interface ReportDecision {
  decision: DecisionKind;
  title: string;
  rationale: string;
  by: string;
  badgeCls: string;
  dot: string;
}

export interface ReportsData {
  found: boolean;
  id: string;
  eyebrowTitle: string; // one-pager big title
  deckTitle: string; // board slide-1 title (may include a line break via \n)
  subtitle: string;
  sourceLabel: string;
  boardDate: string; // "April 2026"
  weightsLabel: string;
  total: number;
  // stat grid
  cap: number;
  capNote: string;
  capNoteReduction: boolean; // red when a reduction
  funded: number;
  fundedNote: string;
  committed: number;
  decisions: ReportDecision[];
  approvedCount: number;
  deferredCount: number;
  otherCount: number;
  // what-changed + slide 1 cards
  changeTitle: string;
  changeSummary: string;
  cards: [string, string][];
  // before/after (slide 3)
  hasComparison: boolean;
  beforeLabel: string;
  beforeFunded: number;
  beforeBarPct: number;
  afterLabel: string;
  afterFunded: number;
  afterBarPct: number;
  crossed: number;
  crossedBarPct: number;
  slide3Title: string;
  slide3Note: string;
}

const METHOD = 'Four-dimension scoring → composite + confidence, ranked cumulatively against the capital cap. Full audit trail in the DVA decision record.';

/** Enumerate the report library (newest first), drawn from the spine's runs + scenarios. */
export function listReports(): ReportEntry[] {
  const runs = getRuns();
  const scen = getScenarios().find((s) => s.name === 'Q2 Budget Reduction');
  const q1 = runs[0];
  const mo6 = runs[2];

  const entries: ReportEntry[] = [];
  if (mo6) entries.push({ id: 'rep-6mo', title: '+6-Month Actuals Review', date: monthYearShort(mo6.created_at), source: `Run · ${mo6.label}`, kind: 'run', types: ['Exec One-Pager · PDF', 'Board Rationale · PPTX'], runId: mo6.run_id });
  if (scen) entries.push({ id: 'rep-q2', title: 'Q2 Capital Reprioritization', date: monthYearShort(runs[1]?.created_at ?? scen.created_at), source: `Saved scenario · ${scen.name}`, kind: 'scenario', types: ['Exec One-Pager · PDF', 'Board Rationale · PPTX'], scenarioId: scen.scenario_id });
  if (q1) entries.push({ id: 'rep-q1', title: 'Q1 2026 Baseline Planning Summary', date: monthYearShort(q1.created_at), source: `Run · ${q1.label}`, kind: 'run', types: ['Exec One-Pager · PDF'], runId: q1.run_id });
  return entries; // +6mo, Q2, Q1 (newest first)
}

/** Default-selected report (the real Q2 one). */
export const DEFAULT_REPORT_ID = 'rep-q2';

const fundedAsk = (runId: string): { funded: number; committed: number } => {
  const asof = getPortfolioAsOf(runId);
  const asks = getCapitalAsks();
  const fundedInits = (asof?.initiatives ?? []).filter((i) => i.funded);
  const committed = Math.round(fundedInits.reduce((a, i) => a + (asks[i.use_case_id] ?? 0), 0) * 10) / 10;
  return { funded: asof?.rollup.funded_count ?? 0, committed };
};

const mapDecisions = (runId: string): ReportDecision[] =>
  buildDecisionTimeline()
    .filter((e) => e.kind === 'decision' && e.run_id === runId)
    .map((d) => ({ decision: d.decision, title: d.title, rationale: d.rationale, by: d.by, badgeCls: DECISION_BADGE[d.decision], dot: DECISION_DOT[d.decision] }));

function emptyReport(): ReportsData {
  return {
    found: false, id: '', eyebrowTitle: '—', deckTitle: '', subtitle: '', sourceLabel: '', boardDate: '', weightsLabel: '',
    total: 0, cap: 0, capNote: '', capNoteReduction: false, funded: 0, fundedNote: '', committed: 0,
    decisions: [], approvedCount: 0, deferredCount: 0, otherCount: 0, changeTitle: '', changeSummary: '', cards: [],
    hasComparison: false, beforeLabel: '', beforeFunded: 0, beforeBarPct: 0, afterLabel: '', afterFunded: 0, afterBarPct: 0,
    crossed: 0, crossedBarPct: 0, slide3Title: '', slide3Note: '',
  };
}

function counts(decisions: ReportDecision[]) {
  return {
    approvedCount: decisions.filter((d) => d.decision === 'APPROVE').length,
    deferredCount: decisions.filter((d) => d.decision === 'DEFER').length,
    otherCount: decisions.filter((d) => d.decision !== 'APPROVE' && d.decision !== 'DEFER').length,
  };
}

/** The real Q2 scenario report (unchanged numbers from Phase 6 Part A). */
function buildScenarioReport(entry: ReportEntry): ReportsData {
  const scen = getScenarios().find((s) => s.scenario_id === entry.scenarioId);
  const runs = getRuns();
  const q2Run = runs.find((r) => r.scenario_id === entry.scenarioId) ?? runs[1];
  if (!scen) return emptyReport();

  const baseCap = getRunCap(scen.base_run_id);
  const newCap = scen.levers.budget_cap;
  const capDeltaAbs = Math.round((baseCap - newCap) * 10) / 10;
  const capDeltaPct = Math.round((1 - newCap / baseCap) * 100);
  const capNote = `−${capDeltaPct}% (−$${capDeltaAbs.toFixed(1)}M) vs Q1`;

  const base = loadScenarioBase();
  const result = computeScenario(base, Q2_WEIGHTS, newCap);
  const total = base.base.length;
  const q1Funded = base.q1FundedCount;
  const scenFunded = result.fundedCount;
  const crossed = result.crossingCount;

  const decisions = mapDecisions(q2Run?.run_id ?? '');
  const c = counts(decisions);

  return {
    found: true,
    id: entry.id,
    eyebrowTitle: 'Q2 Capital Reprioritization',
    deckTitle: 'Q2 Capital Reprioritization:\nRationale & Decisions',
    subtitle: `How a ${capDeltaPct}% capital reduction was absorbed without losing the portfolio’s highest-value commitments.`,
    sourceLabel: `Saved scenario “${scen.name}” + decision record`,
    boardDate: monthYearLong(q2Run?.created_at ?? scen.created_at),
    weightsLabel: `${Q2_WEIGHTS.fi} · ${Q2_WEIGHTS.ou} · ${Q2_WEIGHTS.sa} · ${Q2_WEIGHTS.if}`,
    total,
    cap: newCap,
    capNote,
    capNoteReduction: true,
    funded: scenFunded,
    fundedNote: `${crossed} moved below line`,
    committed: result.allocated,
    decisions,
    ...c,
    changeTitle: 'What changed vs Q1',
    changeSummary: `Financial Impact weight raised 25% → 35% under margin pressure; the capital cap was cut ${capNote}. Cost-reduction initiatives rose and ${crossed} initiatives crossed below the funding line — each carries a deferral decision on the record, not a silent disappearance.`,
    cards: [
      ['The change', `Capital cap $${baseCap.toFixed(1)}M → $${newCap.toFixed(1)}M (−${capDeltaPct}%)`],
      ['The method', 'Four-dimension re-scoring, audited'],
      ['The decisions', `${c.approvedCount} approved · ${c.deferredCount} deferred · logged`],
    ],
    hasComparison: true,
    beforeLabel: `Q1 2026 Planning · $${baseCap.toFixed(1)}M`,
    beforeFunded: q1Funded,
    beforeBarPct: Math.round((q1Funded / total) * 100),
    afterLabel: `${scen.name} · $${newCap.toFixed(1)}M`,
    afterFunded: scenFunded,
    afterBarPct: Math.round((scenFunded / total) * 100),
    crossed,
    crossedBarPct: Math.round((crossed / total) * 100),
    slide3Title: 'The cut, absorbed at the margin — not at the top',
    slide3Note: 'Every crossing is attributable to the cap and weight change — and each affected initiative carries a deferral decision with rationale, not a silent disappearance.',
  };
}

/** Seeded run report (Q1 baseline or +6-Month Actual), sourced from that run's portfolio. */
function buildRunReport(entry: ReportEntry): ReportsData {
  const runs = getRuns();
  const idx = runs.findIndex((r) => r.run_id === entry.runId);
  const run = runs[idx];
  if (!run) return emptyReport();
  const asof = getPortfolioAsOf(run.run_id);
  if (!asof) return emptyReport();

  const cap = getRunCap(run.run_id);
  const { funded, committed } = fundedAsk(run.run_id);
  const total = asof.initiatives.length;
  const decisions = mapDecisions(run.run_id);
  const c = counts(decisions);
  const isBaseline = idx === 0;
  const prev = idx > 0 ? runs[idx - 1] : null;
  const weightsLabel = isBaseline ? '25 · 25 · 25 · 25' : '35 · 25 · 20 · 20';

  // Comparison vs the previous run (movement into/out of the funded set).
  let crossed = 0, beforeFunded = 0, hasComparison = false, beforeLabel = '';
  if (prev) {
    const delta = getChangeDelta(prev.run_id, run.run_id);
    crossed = delta?.summary.dropped_below ?? 0;
    beforeFunded = getPortfolioAsOf(prev.run_id)?.rollup.funded_count ?? 0;
    beforeLabel = `${prev.label} · $${getRunCap(prev.run_id).toFixed(1)}M`;
    hasComparison = true;
  }

  const capNote = isBaseline
    ? 'baseline cap'
    : cap > getRunCap(prev!.run_id)
      ? `restored to $${cap.toFixed(1)}M (+$${(cap - getRunCap(prev!.run_id)).toFixed(1)}M vs prior)`
      : `$${cap.toFixed(1)}M`;

  const changeSummary = isBaseline
    ? `Initial baseline scoring of ${total} initiatives at equal weights against the $${cap.toFixed(1)}M cap. ${c.approvedCount} approved, ${c.deferredCount} deferred, ${c.otherCount} held pending prerequisites — each logged with rationale.`
    : `Re-scored against observed actuals; confidence rose portfolio-wide and the cap returned to $${cap.toFixed(1)}M. ${crossed} initiatives moved below the line since ${prev?.label}; ${c.otherCount} removed from the portfolio, each with a decision on the record.`;

  return {
    found: true,
    id: entry.id,
    eyebrowTitle: isBaseline ? 'Q1 2026 Baseline Planning' : '+6-Month Actuals Review',
    deckTitle: isBaseline ? 'Q1 2026 Baseline Planning:\nPortfolio Established' : '+6-Month Actuals:\nDid the Plan Hold?',
    subtitle: isBaseline
      ? `Establishing the baseline portfolio: ${total} initiatives scored, ${funded} funded under the $${cap.toFixed(1)}M cap.`
      : `Re-scoring the funded portfolio against observed data — what held, what moved, what was removed.`,
    sourceLabel: `Run · ${run.label}`,
    boardDate: monthYearLong(run.created_at),
    weightsLabel,
    total,
    cap,
    capNote,
    capNoteReduction: false,
    funded,
    fundedNote: hasComparison ? `${crossed} moved below line` : 'of the ranked portfolio',
    committed,
    decisions,
    ...c,
    changeTitle: isBaseline ? 'Baseline scope' : `What changed vs ${prev?.label}`,
    changeSummary,
    cards: [
      ['The run', `${run.label} · $${cap.toFixed(1)}M cap · ${funded} funded`],
      ['The method', 'Four-dimension scoring, audited'],
      ['The decisions', `${c.approvedCount} approved · ${c.deferredCount} deferred · ${c.otherCount} other`],
    ],
    hasComparison,
    beforeLabel,
    beforeFunded,
    beforeBarPct: Math.round((beforeFunded / total) * 100),
    afterLabel: `${run.label} · $${cap.toFixed(1)}M`,
    afterFunded: funded,
    afterBarPct: Math.round((funded / total) * 100),
    crossed,
    crossedBarPct: Math.round((crossed / total) * 100),
    slide3Title: isBaseline ? 'The baseline funded portfolio' : 'How the funded portfolio moved',
    slide3Note: isBaseline
      ? `${funded} of ${total} initiatives funded under the $${cap.toFixed(1)}M cap — the reference every later run is compared against.`
      : `Movement since the prior run is attributable to re-scoring and the restored cap; every change carries a decision on the record.`,
  };
}

/** Build the rendered report for a library entry id. */
export function getReport(id: string): ReportsData {
  const entry = listReports().find((e) => e.id === id) ?? listReports().find((e) => e.id === DEFAULT_REPORT_ID);
  if (!entry) return emptyReport();
  return entry.kind === 'scenario' ? buildScenarioReport(entry) : buildRunReport(entry);
}
